import React, { useEffect, useRef, useState } from "react";
import { Upload, Trash2, ImageIcon } from "lucide-react";
import { getSettings, updateSettings } from "@/lib/api";
import api from "@/lib/api";
import { useI18n } from "@/i18n";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const Field = ({ label, children }) => (
  <div className="grid gap-1.5"><Label className="text-xs font-medium text-slate-600">{label}</Label>{children}</div>
);

const Settings = () => {
  const { t } = useI18n();
  const [s, setS] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => { getSettings().then(setS); }, []);
  if (!s) return <div className="text-muted-foreground">{t("common.loading")}</div>;

  const set = (k) => (e) => setS({ ...s, [k]: e.target.value });

  const save = async () => {
    const payload = { ...s, default_tax_rate: Number(s.default_tax_rate) || 0 };
    delete payload._id;
    await updateSettings(payload);
    toast.success(t("settings.saved"));
  };

  const onPick = () => fileRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/settings/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setS({ ...s, logo: data.logo });
      toast.success(t("settings.logoSaved"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    }
    e.target.value = "";
  };

  const removeLogo = async () => {
    await api.delete("/settings/logo");
    setS({ ...s, logo: "" });
    toast.success(t("common.delete"));
  };

  return (
    <div className="mx-auto max-w-2xl">
      <PageHeader title={t("settings.title")} />
      <Card className="space-y-6 p-6">
        <div className="grid gap-2">
          <Label className="text-xs font-medium text-slate-600">{t("settings.logo")}</Label>
          <div className="flex items-center gap-4">
            <div className="flex h-20 w-40 items-center justify-center overflow-hidden rounded-lg border border-dashed border-border bg-slate-50" data-testid="logo-preview">
              {s.logo ? (
                <img src={s.logo} alt="logo" className="max-h-full max-w-full object-contain" />
              ) : (
                <ImageIcon className="h-6 w-6 text-slate-300" />
              )}
            </div>
            <div className="flex flex-col gap-2">
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="logo-file-input" />
              <Button type="button" variant="outline" size="sm" onClick={onPick} data-testid="upload-logo-button"><Upload className="mr-1.5 h-4 w-4" /> {t("settings.uploadLogo")}</Button>
              {s.logo && <Button type="button" variant="ghost" size="sm" onClick={removeLogo} data-testid="remove-logo-button" className="text-rose-600 hover:text-rose-700"><Trash2 className="mr-1.5 h-4 w-4" /> {t("common.delete")}</Button>}
            </div>
          </div>
          <p className="text-xs text-muted-foreground">{t("settings.logoHint")}</p>
        </div>
        <div className="border-t border-border pt-6" />
        <Field label={t("settings.company")}><Input data-testid="settings-company-input" value={s.company_name || ""} onChange={set("company_name")} /></Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label={t("customer.email")}><Input data-testid="settings-email-input" value={s.email || ""} onChange={set("email")} /></Field>
          <Field label={t("customer.phone")}><Input data-testid="settings-phone-input" value={s.phone || ""} onChange={set("phone")} /></Field>
        </div>
        <Field label={t("customer.address")}><Input data-testid="settings-address-input" value={s.address || ""} onChange={set("address")} /></Field>
        <div className="grid grid-cols-3 gap-4">
          <Field label={t("customer.postalCode")}><Input data-testid="settings-postal-input" value={s.postal_code || ""} onChange={set("postal_code")} /></Field>
          <Field label={t("customer.city")}><Input data-testid="settings-city-input" value={s.city || ""} onChange={set("city")} /></Field>
          <Field label={t("customer.country")}><Input data-testid="settings-country-input" value={s.country || ""} onChange={set("country")} /></Field>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Field label={t("customer.taxId")}><Input data-testid="settings-taxid-input" value={s.tax_id || ""} onChange={set("tax_id")} /></Field>
          <Field label={t("settings.defaultTax")}><Input data-testid="settings-defaulttax-input" type="number" step="0.1" value={s.default_tax_rate} onChange={set("default_tax_rate")} /></Field>
        </div>
        <div className="border-t border-border pt-6">
          <p className="mb-4 text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">{t("settings.bank")}</p>
          <div className="grid grid-cols-2 gap-4">
            <Field label={t("settings.bankName")}><Input data-testid="settings-bankname-input" value={s.bank_name || ""} onChange={set("bank_name")} /></Field>
            <Field label={t("settings.iban")}><Input data-testid="settings-iban-input" value={s.iban || ""} onChange={set("iban")} /></Field>
          </div>
        </div>
        <div className="flex justify-end"><Button onClick={save} data-testid="save-settings-button">{t("common.save")}</Button></div>
      </Card>
    </div>
  );
};

export default Settings;
